import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  MdFeedback,
  MdError,
  MdWarning,
  MdDescription,
  MdChat,
  MdCheck,
  MdClose,
  MdStar,
  MdStarBorder,
  MdLaunch,
  MdExpandMore,
  MdExpandLess,
  MdSearch,
  MdPerson,
  MdSchool,
  MdCheckCircle,
  MdCancel,
  MdHelp
} from 'react-icons/md';
import { hasInstructorPermissions } from '../utils/permissions';
import { useClass } from '../utils/ClassContext';
import FeedbackApi from '../utils/api/FeedbackApi';

const FeedbackLog = ({ username, userRole, onNavigate }) => {
  const { selectedClass, getClassContext } = useClass();
  const memoizedGetClassContext = useCallback(() => getClassContext(), [getClassContext]);

  // --- Student search state (mirrors ActivityViewer) ---
  const [allUsers, setAllUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentSearch, setStudentSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const searchRef = useRef(null);

  // --- Feedback list for selected student ---
  const [feedbacks, setFeedbacks] = useState([]);
  const [feedbacksLoading, setFeedbacksLoading] = useState(false);

  // --- Selected session detail ---
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [feedbackDetails, setFeedbackDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState(null);

  // --- Expanded state for session detail sections ---
  const [expandedSections, setExpandedSections] = useState({
    erdNotation: true,
    aiFeedback: true,
    scaffoldStages: true,
    qaInteractions: true,
    erdDiagram: false,
  });
  const [expandedStages, setExpandedStages] = useState({});

  const [error, setError] = useState(null);

  // -----------------------------------------------------------------------
  // Load users on mount / class change
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (!hasInstructorPermissions(userRole)) {
      setError('Access denied. This page is for instructors and TAs only.');
      return;
    }
    if (!userRole || !username) return;

    const fetchUsers = async () => {
      try {
        setUsersLoading(true);
        setError(null);
        const classContext = memoizedGetClassContext();
        const data = await FeedbackApi.getActiveUsers(classContext);
        setAllUsers(data.users || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setUsersLoading(false);
      }
    };
    fetchUsers();
  }, [username, userRole, selectedClass, memoizedGetClassContext]);

  // -----------------------------------------------------------------------
  // Load feedback sessions when student changes
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (!selectedStudent) {
      setFeedbacks([]);
      setSelectedFeedback(null);
      setFeedbackDetails(null);
      return;
    }
    const fetchFeedbacks = async () => {
      try {
        setFeedbacksLoading(true);
        const classContext = memoizedGetClassContext();
        const data = await FeedbackApi.getUserFeedback(selectedStudent.username, classContext);
        const sorted = (data.feedbacks || []).sort((a, b) => new Date(b[4]) - new Date(a[4]));
        setFeedbacks(sorted);
        setSelectedFeedback(null);
        setFeedbackDetails(null);
      } catch (err) {
        setError(err.message);
      } finally {
        setFeedbacksLoading(false);
      }
    };
    fetchFeedbacks();
  }, [selectedStudent, memoizedGetClassContext]);

  // -----------------------------------------------------------------------
  // Fetch session details
  // -----------------------------------------------------------------------
  const fetchDetails = async (feedbackId) => {
    try {
      setDetailsLoading(true);
      setDetailsError(null);
      setFeedbackDetails(null);
      setExpandedStages({});
      setExpandedSections({ erdDiagram: true, aiFeedback: true, scaffoldStages: true, qaInteractions: true, fullErdDiagram: false, erdNotation: false });
      const classContext = memoizedGetClassContext();
      const data = await FeedbackApi.getFeedbackDetails(feedbackId, classContext);
      if (data && data.feedback) {
        setFeedbackDetails(data.feedback);
      } else {
        setDetailsError('Server returned empty feedback details.');
      }
    } catch (err) {
      console.error('[FeedbackLog] fetchDetails error:', err);
      setDetailsError(err.message || 'Failed to load session details.');
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleSessionClick = (fb) => {
    setSelectedFeedback(fb);
    fetchDetails(fb[0]);
  };

  // -----------------------------------------------------------------------
  // Student search helpers
  // -----------------------------------------------------------------------
  const filteredUsers = allUsers.filter(u =>
    u.username.toLowerCase().includes(studentSearch.toLowerCase())
  );

  const handleStudentSelect = (user) => {
    setSelectedStudent(user);
    setStudentSearch(user.username);
    setShowDropdown(false);
  };

  const handleClearStudent = () => {
    setSelectedStudent(null);
    setStudentSearch('');
    setFeedbacks([]);
    setSelectedFeedback(null);
    setFeedbackDetails(null);
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // -----------------------------------------------------------------------
  // Helpers
  // -----------------------------------------------------------------------
  const formatDate = (ds) => {
    try {
      return new Date(ds).toLocaleString('en-US', { timeZone: 'America/Chicago' }) + ' CT';
    } catch { return ds; }
  };

  const toggleSection = (key) =>
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));

  const toggleStage = (idx) =>
    setExpandedStages(prev => ({ ...prev, [idx]: !prev[idx] }));

  const renderFeedbackText = (text) => {
    if (!text) return <p className="text-gray-500 italic">No feedback text available.</p>;
    return text.split('\n').filter(l => l.trim()).map((line, i) => (
      <p key={i} className="mb-2 text-gray-700">{line}</p>
    ));
  };

  const CorrectnessIcon = ({ value }) => {
    if (value === true || value === 'correct')
      return <MdCheckCircle className="text-green-500 inline-block mr-1" title="Correct" />;
    if (value === false || value === 'incorrect')
      return <MdCancel className="text-red-400 inline-block mr-1" title="Incorrect" />;
    return <MdHelp className="text-gray-400 inline-block mr-1" title="Unknown" />;
  };

  // Decode a base64 SVG string and return JSX, or null if invalid/empty
  const renderSvgBase64 = (b64, altText = 'ERD Diagram') => {
    if (!b64) return null;
    try {
      const decoded = atob(b64);
      if (decoded.includes('<svg')) {
        return (
          <div className="bg-white p-4 rounded border overflow-auto text-center">
            <div dangerouslySetInnerHTML={{ __html: decoded }} className="w-full flex justify-center" />
          </div>
        );
      }
      return (
        <div className="bg-white p-4 rounded border overflow-auto text-center">
          <img src={`data:image/svg+xml;base64,${b64}`} alt={altText} className="max-w-full h-auto mx-auto" />
        </div>
      );
    } catch {
      return null;
    }
  };

  const renderERDDiagram = (details) => {
    const svg = renderSvgBase64(details.erd_diagram);
    if (svg) return svg;
    return <p className="text-gray-500 text-sm italic">No diagram available.</p>;
  };

  const CollapsibleSection = ({ title, sectionKey, children, badge }) => {
    const open = expandedSections[sectionKey];
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <button
          onClick={() => toggleSection(sectionKey)}
          className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <span className="font-semibold text-gray-800">{title}</span>
            {badge != null && (
              <span className="text-xs bg-blue-100 text-blue-700 rounded-full px-2 py-0.5">{badge}</span>
            )}
          </div>
          {open ? <MdExpandLess className="text-gray-500" /> : <MdExpandMore className="text-gray-500" />}
        </button>
        {open && <div className="p-4 border-t border-gray-100">{children}</div>}
      </div>
    );
  };

  // -----------------------------------------------------------------------
  // Access check
  // -----------------------------------------------------------------------
  if (error && error.includes('Access denied')) {
    return (
      <div className="h-full flex items-center justify-center p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-lg">
          <div className="flex items-center">
            <MdWarning className="text-red-600 text-xl mr-3" />
            <div>
              <h3 className="font-medium text-red-800">Access Denied</h3>
              <p className="text-red-700 mt-1">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------
  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-md m-6 mb-3 p-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-800 mb-1">Feedback Log</h1>
            <p className="text-gray-600 text-sm">
              View feedback sessions, ERD notation, and scaffolding interactions per student
              {selectedClass && selectedClass.class_id !== 'all'
                ? ` — ${selectedClass.class_name}`
                : ''}
            </p>
          </div>
          <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-500 rounded-full flex items-center justify-center flex-shrink-0">
            <MdFeedback className="text-white" />
          </div>
        </div>

        {/* Student Search */}
        <div className="mt-3 relative" ref={searchRef}>
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder={usersLoading ? 'Loading students…' : 'Search student by username…'}
                value={studentSearch}
                onChange={e => { setStudentSearch(e.target.value); setShowDropdown(true); }}
                onFocus={() => setShowDropdown(true)}
                disabled={usersLoading}
                className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 disabled:bg-gray-100"
              />
            </div>
            {selectedStudent && (
              <button
                onClick={handleClearStudent}
                className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 hover:text-red-600 border border-gray-300 rounded-lg hover:border-red-300 transition-colors"
              >
                <MdClose className="text-base" />
                Clear
              </button>
            )}
          </div>

          {/* Dropdown */}
          {showDropdown && !usersLoading && filteredUsers.length > 0 && (
            <div className="absolute z-50 top-full mt-1 left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
              {filteredUsers.map(user => (
                <button
                  key={user.username}
                  onClick={() => handleStudentSelect(user)}
                  className={`w-full flex items-center gap-2 px-4 py-2 text-sm text-left hover:bg-blue-50 transition-colors ${
                    selectedStudent?.username === user.username ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                  }`}
                >
                  <MdPerson className="text-gray-400 flex-shrink-0" />
                  <span className="truncate">{user.username}</span>
                  {user.role && (
                    <span className="ml-auto text-xs text-gray-400 flex-shrink-0">{user.role}</span>
                  )}
                </button>
              ))}
            </div>
          )}

          {showDropdown && !usersLoading && studentSearch && filteredUsers.length === 0 && (
            <div className="absolute z-50 top-full mt-1 left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg px-4 py-3 text-sm text-gray-500">
              No students found matching "{studentSearch}"
            </div>
          )}
        </div>

        {/* Selected student chip */}
        {selectedStudent && (
          <div className="mt-2 flex items-center gap-2">
            <MdSchool className="text-blue-500" />
            <span className="text-sm font-medium text-blue-700">{selectedStudent.username}</span>
            {feedbacksLoading && (
              <span className="text-xs text-gray-500">Loading sessions…</span>
            )}
            {!feedbacksLoading && (
              <span className="text-xs text-gray-500">{feedbacks.length} feedback session(s)</span>
            )}
          </div>
        )}
      </div>

      {/* Body: two-panel layout */}
      {!selectedStudent ? (
        <div className="flex-1 flex items-center justify-center text-gray-500 m-6">
          <div className="text-center">
            <MdSearch className="text-5xl mb-3 text-gray-300 mx-auto" />
            <p className="text-lg font-medium text-gray-600">Select a student to view feedback</p>
            <p className="text-sm text-gray-400 mt-1">Search for a student above to load their sessions</p>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex bg-white rounded-lg shadow-md mx-6 mb-6 overflow-hidden min-h-0">
          {/* Left: session list */}
          <div className="w-72 flex-shrink-0 border-r border-gray-200 bg-gray-50 flex flex-col">
            <div className="p-3 border-b border-gray-200 bg-white flex-shrink-0">
              <h2 className="text-sm font-semibold text-gray-700">
                Sessions ({feedbacks.length})
              </h2>
            </div>
            <div className="flex-1 overflow-y-auto min-h-0">
              {feedbacksLoading ? (
                <div className="flex items-center justify-center h-24">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500 mr-2" />
                  <span className="text-sm text-gray-500">Loading…</span>
                </div>
              ) : feedbacks.length === 0 ? (
                <div className="p-6 text-center text-gray-500 text-sm">
                  No feedback sessions for this student.
                </div>
              ) : (
                <div className="p-2 space-y-2">
                  {feedbacks.map((fb) => {
                    const [fbId, , env, rel, date] = fb;
                    const isSelected = selectedFeedback && selectedFeedback[0] === fbId;
                    return (
                      <button
                        key={fbId}
                        onClick={() => handleSessionClick(fb)}
                        className={`w-full text-left p-3 rounded-lg border transition-all ${
                          isSelected
                            ? 'border-blue-400 bg-blue-50 shadow-sm'
                            : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                        }`}
                      >
                        <div className="text-xs text-gray-500 mb-1">{formatDate(date)}</div>
                        {env && (
                          <div className="text-sm font-medium text-gray-800 truncate">{env}</div>
                        )}
                        {rel && (
                          <div className="text-xs text-gray-500 truncate mt-0.5">{rel}</div>
                        )}
                        {isSelected && (
                          <div className="mt-1.5 flex items-center text-xs text-blue-600">
                            <MdCheck className="mr-0.5" /> Selected
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Right: session detail */}
          <div className="flex-1 min-h-0 flex flex-col bg-white">
            {!selectedFeedback ? (
              <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
                <MdDescription className="text-5xl mb-3" />
                <p className="text-base">Select a session to view details</p>
              </div>
            ) : detailsLoading ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mr-3" />
                <span className="text-gray-600">Loading details…</span>
              </div>
            ) : detailsError ? (
              <div className="flex-1 flex flex-col items-center justify-center text-red-400 p-6">
                <MdError className="text-5xl mb-3" />
                <p className="font-medium text-red-700 mb-1">Failed to load session details</p>
                <p className="text-sm text-red-500 text-center max-w-sm">{detailsError}</p>
              </div>
            ) : !feedbackDetails ? (
              <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
                <MdError className="text-5xl mb-3" />
                <p>No details available.</p>
              </div>
            ) : (
              <div className="flex-1 min-h-0 overflow-y-auto">
                <div className="p-5 space-y-4">
                  {/* Header row */}
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-sm text-gray-500">{formatDate(feedbackDetails.date)}</p>
                      {feedbackDetails.environment && (
                        <p className="font-semibold text-gray-800">{feedbackDetails.environment}</p>
                      )}
                      {feedbackDetails.relationship && (
                        <p className="text-sm text-gray-600">{feedbackDetails.relationship}</p>
                      )}
                    </div>
                    <button
                      onClick={() => {
                        if (onNavigate && selectedFeedback) {
                          onNavigate('discussions', {
                            feedbackId: selectedFeedback[0],
                            environment: feedbackDetails.environment,
                            relationship: feedbackDetails.relationship,
                            username: feedbackDetails.username,
                            autoExpand: true,
                          });
                        }
                      }}
                      className="flex-shrink-0 inline-flex items-center px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      <MdChat className="mr-1.5" />
                      Discussion
                      <MdLaunch className="ml-1.5 text-xs" />
                    </button>
                  </div>

                  {/* Filtered ERD diagram for the analyzed relationship */}
                  {feedbackDetails.filtered_svg && (
                    <CollapsibleSection
                      title={`Relationship ERD${feedbackDetails.relationship ? ` — ${feedbackDetails.relationship}` : ''}`}
                      sectionKey="erdDiagram"
                    >
                      <div style={{ zoom: 0.5 }}>
                        {renderSvgBase64(feedbackDetails.filtered_svg, 'Filtered ERD')}
                      </div>
                    </CollapsibleSection>
                  )}

                  {/* Q&A Interactions (for qa_log sessions or sessions with Q&A) */}
                  {feedbackDetails.qa_interactions && feedbackDetails.qa_interactions.length > 0 && (
                    <CollapsibleSection
                      title="Q&A Interactions"
                      sectionKey="qaInteractions"
                      badge={feedbackDetails.qa_interactions.length}
                    >
                      <div className="space-y-3">
                        {feedbackDetails.qa_interactions.map((item, idx) => (
                          <div key={idx} className="border border-gray-200 rounded-lg p-3 bg-white space-y-2">
                            <div className="flex items-start gap-2">
                              <span className="text-xs font-semibold text-indigo-500 mt-0.5 w-5 flex-shrink-0">Q</span>
                              <p className="text-sm font-medium text-gray-800 flex-1">{item.question}</p>
                            </div>
                            <div className="flex items-start gap-2">
                              <span className="text-xs font-semibold text-green-500 mt-0.5 w-5 flex-shrink-0">A</span>
                              <p className="text-sm text-gray-700 flex-1">{item.answer}</p>
                            </div>
                            {item.timestamp && (
                              <p className="text-xs text-gray-400 text-right">{formatDate(item.timestamp)}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </CollapsibleSection>
                  )}

                  {/* AI Feedback (hidden for pure Q&A log sessions) */}
                  {feedbackDetails.type !== 'qa_log' && (
                  <CollapsibleSection title="AI Feedback" sectionKey="aiFeedback">
                    <div className="bg-blue-50 rounded p-4 border border-blue-100">
                      {renderFeedbackText(feedbackDetails.feedback)}
                    </div>
                  </CollapsibleSection>
                  )}

                  {/* Scaffold Stages */}
                  {feedbackDetails.scaffold_stages && feedbackDetails.scaffold_stages.length > 0 && (
                    <CollapsibleSection
                      title="Scaffolding Interactions"
                      sectionKey="scaffoldStages"
                      badge={feedbackDetails.scaffold_stages.length}
                    >
                      <div className="space-y-3">
                        {feedbackDetails.scaffold_stages.map((stage, idx) => {
                          const open = expandedStages[idx] !== false; // default open
                          const hasQuestions = stage.questions && stage.questions.length > 0;
                          const correctCount = hasQuestions
                            ? stage.questions.filter(q => q.is_correct === true || q.is_correct === 'correct').length
                            : 0;

                          return (
                            <div key={idx} className="border border-gray-200 rounded-lg overflow-hidden">
                              <button
                                onClick={() => toggleStage(idx)}
                                className="w-full flex items-center justify-between px-4 py-2 bg-gray-50 hover:bg-gray-100 transition-colors"
                              >
                                <div className="flex items-center gap-3">
                                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                    Stage {idx + 1}
                                  </span>
                                  {stage.stage_type && (
                                    <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded">
                                      {stage.stage_type}
                                    </span>
                                  )}
                                  {hasQuestions && (
                                    <span className="text-xs text-gray-500">
                                      {correctCount}/{stage.questions.length} correct
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  {stage.timestamp && (
                                    <span className="text-xs text-gray-400 hidden sm:inline">
                                      {formatDate(stage.timestamp)}
                                    </span>
                                  )}
                                  {open
                                    ? <MdExpandLess className="text-gray-400" />
                                    : <MdExpandMore className="text-gray-400" />}
                                </div>
                              </button>

                              {open && (
                                <div className="p-4 space-y-4">
                                  {!hasQuestions ? (
                                    <p className="text-sm text-gray-400 italic">No questions recorded for this stage.</p>
                                  ) : (
                                    stage.questions.map((q, qi) => (
                                      <div key={qi} className="border border-gray-100 rounded-lg p-3 bg-white">
                                        {/* Question text */}
                                        <div className="flex items-start gap-2 mb-2">
                                          <span className="text-xs font-semibold text-gray-400 mt-0.5 w-5 flex-shrink-0">Q{qi + 1}</span>
                                          <p className="text-sm font-medium text-gray-800 flex-1">{q.question_text}</p>
                                        </div>

                                        {/* MCQ options */}
                                        {q.resource_type === 'mcq' && q.options && Object.keys(q.options).length > 0 && (
                                          <div className="ml-7 mb-2 space-y-1">
                                            {Object.entries(q.options).map(([key, val]) => {
                                              const isChosen = q.student_answer === key || q.student_answer === val;
                                              return (
                                                <div
                                                  key={key}
                                                  className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${
                                                    isChosen
                                                      ? (q.is_correct === true || q.is_correct === 'correct')
                                                        ? 'bg-green-50 border border-green-200 text-green-800'
                                                        : (q.is_correct === false || q.is_correct === 'incorrect')
                                                          ? 'bg-red-50 border border-red-200 text-red-800'
                                                          : 'bg-blue-50 border border-blue-200 text-blue-800'
                                                      : 'bg-gray-50 text-gray-600'
                                                  }`}
                                                >
                                                  <span className="font-semibold">{key}.</span>
                                                  <span className="flex-1">{val}</span>
                                                  {isChosen && (
                                                    <CorrectnessIcon value={q.is_correct} />
                                                  )}
                                                </div>
                                              );
                                            })}
                                          </div>
                                        )}

                                        {/* Student answer */}
                                        <div className="ml-7">
                                          <span className="text-xs text-gray-500 font-medium">Student answer: </span>
                                          {q.student_answer ? (
                                            <span className={`text-xs font-medium ${
                                              q.is_correct === true || q.is_correct === 'correct'
                                                ? 'text-green-700'
                                                : q.is_correct === false || q.is_correct === 'incorrect'
                                                  ? 'text-red-600'
                                                  : 'text-gray-700'
                                            }`}>
                                              <CorrectnessIcon value={q.is_correct} />
                                              {q.student_answer}
                                            </span>
                                          ) : (
                                            <span className="text-xs text-gray-400 italic">No answer recorded</span>
                                          )}
                                        </div>
                                      </div>
                                    ))
                                  )}

                                  {/* AI output for this stage */}
                                  {stage.concept_excerpt && (
                                    <div className="border-t border-indigo-100 pt-3 mt-1">
                                      <p className="text-xs font-semibold text-indigo-500 uppercase tracking-wide mb-2">
                                        AI Response
                                      </p>
                                      <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3 text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                                        {stage.concept_excerpt}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </CollapsibleSection>
                  )}


                  {/* Full ERD diagram */}
                  {feedbackDetails.erd_diagram && (
                    <CollapsibleSection title="Full ERD Diagram" sectionKey="fullErdDiagram">
                      <div style={{ zoom: 0.5 }}>
                        {renderERDDiagram(feedbackDetails)}
                      </div>
                    </CollapsibleSection>
                  )}

                  {/* ERD Notation */}
                  {feedbackDetails.erd_plain && (
                    <CollapsibleSection title="ERD Notation" sectionKey="erdNotation">
                      <div className="bg-gray-50 rounded p-3 border border-gray-200">
                        <pre className="text-sm text-gray-700 whitespace-pre-wrap font-mono leading-relaxed">
                          {feedbackDetails.erd_plain}
                        </pre>
                      </div>
                    </CollapsibleSection>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedbackLog;
